import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronUp, ChevronDown, Star, Link2 } from "lucide-react";
import { sigMeta, fmt, cur, pct, UP, DOWN } from "@/lib/format";
import { useWatchlist } from "@/lib/useWatchlist";

const FILTERS = [
  { key: "all", label: "ALL SETUPS" },
  { key: "STRONG_BUY", label: "STRONG BUY" },
  { key: "STRONG_SHORT", label: "STRONG SHORT" },
  { key: "MONITOR_BUY", label: "WATCH BUY" },
  { key: "MONITOR_SHORT", label: "WATCH SHORT" },
];

const COLS = [
  { label: "TICKER", key: "ticker", type: "str" },
  { label: "SIGNAL", key: "signal", type: "signal" },
  { label: "PRICE", key: "price", type: "num" },
  { label: "RSI", key: "rsi", type: "num" },
  { label: "STOCH K/D", key: "stoch_k", type: "num" },
  { label: "MACD", key: "macd_hist", type: "num" },
  { label: "ADX", key: "adx", type: "num" },
  { label: "BB%B", key: "bb_percent_b", type: "num" },
  { label: "P/E", key: "current_pe", type: "num" },
  { label: "5Y DEV", key: "pe_dev_pct", type: "num" },
  { label: "BS", key: "strong_balance_sheet", type: "bool" },
];

const SIGNAL_RANK = { STRONG_SHORT: 0, STRONG_BUY: 1, MONITOR_SHORT: 2, MONITOR_BUY: 3, NEUTRAL: 4 };

const Cell = ({ children, className = "", style }) => (
  <td className={`px-3.5 py-3 font-mono text-[13px] tabular-nums whitespace-nowrap ${className}`} style={style}>{children}</td>
);

export default function SignalBoard({ items, signalFilter, setSignalFilter, selected, onSelect, matchedCount }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [watchOnly, setWatchOnly] = useState(false);
  const { has, toggle, count, code, adopt } = useWatchlist();

  const onSync = () => {
    const entered = window.prompt(
      `Your watchlist sync code:\n\n    ${code}\n\nEnter this code on another device to load the same watchlist.\nOr paste a code here to adopt that watchlist:`,
      ""
    );
    if (entered && entered.trim()) adopt(entered);
  };

  const handleSort = (col) => {
    if (sortKey === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir(col.type === "str" ? "asc" : "desc");
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return items;
    const col = COLS.find((c) => c.key === sortKey);
    const val = (it) => {
      if (col.type === "signal") return SIGNAL_RANK[it.signal] ?? 9;
      if (col.type === "bool") return it[col.key] ? 1 : 0;
      if (col.type === "str") return it.ticker || "";
      const v = it[col.key];
      return v === null || v === undefined ? null : v;
    };
    const arr = [...items];
    arr.sort((a, b) => {
      const av = val(a), bv = val(b);
      if (col.type === "str") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  const visible = watchOnly ? sorted.filter((it) => has(it.ticker)) : sorted;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-[4px] shadow-xs flex flex-col h-full min-h-0" data-testid="signal-board">
      <div className="p-3.5 border-b border-[#E2E8F0] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="font-bold text-[13px] tracking-[0.1em] uppercase text-slate-700">Signal Screener</span>
          <span className="font-mono text-[11px] px-2 py-0.5 rounded-[3px] bg-[#F1F5F9] text-slate-500 tabular-nums">
            {matchedCount} MATCHED
          </span>
        </div>
        <div className="flex gap-1.5 flex-wrap items-center">
          <button
            onClick={() => setWatchOnly((v) => !v)}
            data-testid="signal-filter-watchlist"
            className="font-semibold text-[11px] tracking-wide px-2.5 py-1.5 rounded-[4px] border transition-colors duration-150 inline-flex items-center gap-1"
            style={{
              background: watchOnly ? "#FEF9C3" : "#FFFFFF",
              borderColor: watchOnly ? "#FACC15" : "#E2E8F0",
              color: watchOnly ? "#854D0E" : "#64748B",
            }}
          >
            <Star size={12} fill={watchOnly ? "#EAB308" : "none"} color={watchOnly ? "#EAB308" : "currentColor"} />
            WATCHLIST {count > 0 && `(${count})`}
          </button>
          <button
            onClick={onSync}
            data-testid="watchlist-sync-btn"
            title="Sync watchlist across devices"
            className="font-semibold text-[11px] tracking-wide px-2 py-1.5 rounded-[4px] border border-[#E2E8F0] text-slate-500 hover:text-slate-800 hover:border-[#CBD5E1] transition-colors inline-flex items-center gap-1"
          >
            <Link2 size={12} /> SYNC
          </button>
          {FILTERS.map((f) => {
            const active = signalFilter === f.key;
            const m = f.key !== "all" ? sigMeta(f.key) : null;
            return (
              <button
                key={f.key}
                onClick={() => setSignalFilter(f.key)}
                data-testid={`signal-filter-${f.key}`}
                className="font-semibold text-[11px] tracking-wide px-2.5 py-1.5 rounded-[4px] border transition-colors duration-150"
                style={{
                  background: active ? (m ? m.bg : "#121D28") : "#FFFFFF",
                  borderColor: active ? (m ? m.border : "#121D28") : "#E2E8F0",
                  color: active ? (m ? m.color : "#FFFFFF") : "#64748B",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        <table className="w-full min-w-[1120px] border-collapse">
          <thead className="sticky top-0 bg-[#F8FAFC] z-10">
            <tr className="text-left">
              {COLS.map((c) => {
                const active = sortKey === c.key;
                return (
                  <th
                    key={c.key}
                    onClick={() => handleSort(c)}
                    data-testid={`sort-${c.key}`}
                    className={`px-3.5 py-3 font-semibold text-[12px] tracking-wide uppercase border-b border-[#CBD5E1] whitespace-nowrap cursor-pointer select-none transition-colors ${active ? "text-[#0070BA]" : "text-slate-500 hover:text-slate-800"} ${c.key === "ticker" ? "sticky left-0 z-30 bg-[#F8FAFC]" : ""}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {active ? (
                        sortDir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                      ) : (
                        <ChevronDown size={13} className="opacity-20" />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={11} className="px-3 py-12 text-center text-[14px] text-slate-400">{watchOnly ? "No starred tickers yet. Click the ★ on any row to add." : "No matches for this filter. Run a scan."}</td></tr>
            ) : visible.map((it, idx) => {
              const m = sigMeta(it.signal);
              const active = selected === it.ticker;
              const strong = it.signal === "STRONG_BUY" || it.signal === "STRONG_SHORT";
              return (
                <motion.tr
                  key={it.ticker}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(idx * 0.012, 0.3) }}
                  onClick={() => onSelect(it.ticker)}
                  data-testid={`signal-row-${it.ticker}`}
                  className={`cursor-pointer border-b border-[#F1F5F9] transition-colors duration-150 ${active ? "bg-[#EFF6FF]" : "hover:bg-[#F8FAFC]"}`}
                >
                  <td className="px-3.5 py-3 sticky left-0 z-10 whitespace-nowrap" style={{ background: active ? "#EFF6FF" : "#FFFFFF" }}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggle(it.ticker); }}
                        data-testid={`star-${it.ticker}`}
                        className="shrink-0 text-slate-300 hover:text-[#EAB308] transition-colors"
                        title="Toggle watchlist"
                      >
                        <Star size={14} fill={has(it.ticker) ? "#EAB308" : "none"} color={has(it.ticker) ? "#EAB308" : "currentColor"} />
                      </button>
                      <span className="font-mono text-[14px] font-bold text-slate-900">{it.ticker.replace(".NS", "").replace("^", "")}</span>
                      <span className="text-[10px] text-slate-400 font-normal">{it.asset_type && it.asset_type !== "STOCK" ? it.asset_type : it.market}</span>
                    </div>
                  </td>
                  <td className="px-3.5 py-3">
                    <span
                      className="font-semibold text-[11px] tracking-wide px-2 py-1 rounded-[4px] border whitespace-nowrap inline-flex items-center gap-1"
                      style={{ color: m.color, background: m.bg, borderColor: m.border }}
                    >
                      <span className="text-[8px]">{m.icon}</span>{m.label}
                    </span>
                  </td>
                  <Cell className="text-slate-800 font-semibold">{cur(it.market)}{fmt(it.price)}</Cell>
                  <Cell style={{ color: it.rsi > 80 ? DOWN : it.rsi < 28 ? UP : "#334155" }}>{fmt(it.rsi, 1)}</Cell>
                  <Cell className="text-slate-500">{fmt(it.stoch_k, 0)}/{fmt(it.stoch_d, 0)}</Cell>
                  <Cell style={{ color: it.macd_bull_cross ? UP : "#64748B" }}>{it.macd_bull_cross ? "↑CROSS" : fmt(it.macd_hist, 2)}</Cell>
                  <Cell style={{ color: (it.adx ?? 0) >= 25 ? (it.adx_direction === "BULL" ? UP : DOWN) : "#94A3B8" }}>
                    {fmt(it.adx, 0)}{(it.adx ?? 0) >= 25 ? (it.adx_direction === "BULL" ? "↑" : "↓") : ""}
                  </Cell>
                  <Cell style={{ color: (it.bb_percent_b ?? 0.5) >= 0.9 ? DOWN : (it.bb_percent_b ?? 0.5) <= 0.1 ? UP : "#64748B" }}>
                    {it.bb_percent_b === null || it.bb_percent_b === undefined ? "—" : Math.round(it.bb_percent_b * 100)}
                  </Cell>
                  <Cell className="text-slate-500">{fmt(it.current_pe, 1)}</Cell>
                  <Cell style={{ color: (it.pe_dev_pct ?? 0) >= 50 ? DOWN : (it.pe_dev_pct ?? 0) <= -50 ? UP : "#94A3B8" }}>
                    {pct(it.pe_dev_pct)}
                  </Cell>
                  <Cell style={{ color: it.strong_balance_sheet ? UP : "#CBD5E1" }}>{it.strong_balance_sheet ? "✓" : "·"}</Cell>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3.5 py-1.5 border-t border-[#E2E8F0] text-[11px] text-slate-400 flex items-center gap-1.5">
        <span className="text-slate-300">↔</span> Scroll horizontally · click any column header to sort
      </div>
    </div>
  );
}
